import type React from 'react'
import { useState, useEffect } from 'react'
import {
  SCHOOL_NOT_FOUND,
  LOGIN_PATH,
  BOOKFAIR_SUCCESS_PATH
} from 'routes/paths'
import {
  getLayersPaymentEntityInfoQuery,
  getMaximumHierarchyType,
  getSchoolsByRoot,
  findReaders
} from 'api/queries'
import { activateLayersUserMutation } from 'api/mutations'
import { hotjarEvent } from 'utils/ui-trackers/hotjar'
import { tokens } from '@arvoreeducacao/bonsai-ds'
import useDebounce from 'utils/useDebounce/useDebounce'
import { useToast } from 'utils/hooks/use-toast/useToast'

type UnitObject = {
  id: number
  name: string
}

type ReaderObject = {
  id: number
  name: string
}

export const useSchoolConfirmationLogin = ({ client, history, match }) => {
  const { toastMessage } = useToast()
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
  const [schoolFound, setSchoolFound] = useState<boolean>(false)
  const [schoolRootId, setSchoolRootId] = useState<number | null>(null)
  const [entityName, setEntityName] = useState<string | null>(null)
  const [hierarchyType, setHierarchyType] = useState<string | null>(null)
  const [units, setUnits] = useState<UnitObject[]>([])
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null)
  const [selectedUnitName, setSelectedUnitName] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<string | null>(null)
  const [loadingUnits, setLoadingUnits] = useState<boolean>(false)
  const [readersFound, setReadersFound] = useState<ReaderObject[]>([])
  const [selectedReaderId, setSelectedReaderId] = useState<number | null>(null)
  const [selectedReaderName, setSelectedReaderName] = useState<string>('')
  const [readerSearchTerm, setReaderSearchTerm] = useState<string>('')
  const [noReaderFound, setNoReaderFound] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [loadingReaders, setLoadingReaders] = useState<boolean>(false)

  const debouncedReaderSearchTerm = useDebounce(readerSearchTerm, 500)

  const toggleShowHelpModal = () => {
    setShowHelpModal(!showHelpModal)
    hotjarEvent('not_the_school')
  }

  const handleUnitSelect = (unitId: number, unitName: string) => {
    setSelectedUnitId(unitId)
    setSelectedUnitName(unitName)
    setSelectedYear(null)
  }

  const handleYearSelect = (year: string) => {
    setSelectedYear(year)
    setSelectedReaderId(null)
    setSelectedReaderName('')
    setReaderSearchTerm('')
    setReadersFound([])
    setNoReaderFound(false)
  }

  const eraseField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isMobile = window.screen.width <= tokens.sizes.smScreen
    const hasSelectedReader = selectedReaderId !== null

    if (
      ['Delete', 'Backspace'].includes(event.key) ||
      (isMobile && hasSelectedReader)
    ) {
      setSelectedReaderName('')
      setReaderSearchTerm('')
      setSelectedReaderId(null)
      setNoReaderFound(false)
    }
  }

  const handleReaderSearchChange = (term: string) => {
    setSelectedReaderName(term)
    setReaderSearchTerm(term)
    setSelectedReaderId(null)
  }

  useEffect(() => {
    if (
      !selectedUnitId ||
      !debouncedReaderSearchTerm ||
      debouncedReaderSearchTerm.trim().length === 0
    ) {
      setReadersFound([])
      setLoadingReaders(false)
      return
    }

    const termArray = debouncedReaderSearchTerm.split(' ')
    const hasOnlyOneWord = termArray.length === 1
    const isSecondWordEmpty =
      !hasOnlyOneWord && termArray[1] && termArray[1].length < 1

    if (hasOnlyOneWord || isSecondWordEmpty) {
      setReadersFound([])
      setLoadingReaders(false)
      return
    }

    setLoadingReaders(true)

    client
      .query({
        query: findReaders,
        variables: {
          entityId: selectedUnitId,
          term: debouncedReaderSearchTerm
        }
      })
      .then(res => {
        const readers = res.data.findReaders || []
        if (!readers.length) {
          setReadersFound([])
          setNoReaderFound(true)
        } else {
          setReadersFound(readers)
          setNoReaderFound(false)
        }
        setLoadingReaders(false)
      })
      .catch(() => {
        setNoReaderFound(true)
        setReadersFound([])
        setLoadingReaders(false)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedReaderSearchTerm, selectedUnitId])

  const handleReaderClick = (reader: ReaderObject) => {
    setSelectedReaderId(reader.id)
    setSelectedReaderName(reader.name)
    setReadersFound([])
  }

  const handleNextPage = async () => {
    if (
      !selectedReaderId ||
      !selectedUnitId ||
      !selectedYear ||
      !match.params.code
    ) {
      return
    }

    setIsSubmitting(true)

    try {
      const result = await client.mutate({
        mutation: activateLayersUserMutation,
        variables: {
          userId: selectedReaderId,
          entityId: selectedUnitId,
          degree: selectedYear,
          externalTransactionId: match.params.code
        }
      })

      if (result.data?.activateLayersUser) {
        history.push(BOOKFAIR_SUCCESS_PATH)
      }
    } catch (err) {
      console.error('Error activating layers user:', err)
      toastMessage({
        type: 'error',
        message:
          'Ocorreu um erro ao processar sua solicitação. Por favor, entre em contato com o suporte.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    client
      .query({
        query: getLayersPaymentEntityInfoQuery,
        variables: {
          externalTransactionId: match.params.code || ''
        }
      })
      .then(res => {
        const rootId = res.data.layersPaymentEntityInfo?.arvoreEntityId
        const name = res.data.layersPaymentEntityInfo?.entityName
        setSchoolRootId(rootId)
        setEntityName(name)
        setSchoolFound(true)
      })
      .catch(err => {
        const errorMessage =
          err?.message || err?.graphQLErrors?.[0]?.message || ''
        const isLinkAlreadyUsed = errorMessage.includes('link_already_used')

        if (isLinkAlreadyUsed) {
          history.push(LOGIN_PATH)
        } else {
          history.push(SCHOOL_NOT_FOUND)
        }
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!schoolRootId) return

    setLoadingUnits(true)

    client
      .query({
        query: getMaximumHierarchyType,
        variables: {
          rootId: schoolRootId
        }
      })
      .then(res => {
        const { hierarchyType: type } = res.data.maximumHierarchyType
        setHierarchyType(type)

        return client
          .query({
            query: getSchoolsByRoot,
            variables: {
              rootId: schoolRootId,
              type: type
            }
          })
          .then(schoolsRes => {
            const schools = schoolsRes.data.schoolsByRoot || []
            setUnits(schools)

            if (type === 'school' && entityName) {
              const school =
                schools.find(s => s.name === entityName) || schools[0]
              if (school) {
                setSelectedUnitId(school.id)
                setSelectedUnitName(entityName)
              }
            }

            setLoadingUnits(false)
          })
      })
      .catch(err => {
        setLoadingUnits(false)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolRootId, entityName])

  const handlers = {
    toggleShowHelpModal,
    handleUnitSelect,
    handleYearSelect,
    handleReaderSearchChange,
    eraseField,
    handleReaderClick,
    handleNextPage
  }
  const states = {
    schoolFound,
    schoolRootId,
    entityName,
    hierarchyType,
    units,
    selectedUnitId,
    selectedUnitName,
    selectedYear,
    readersFound,
    selectedReaderId,
    selectedReaderName,
    noReaderFound,
    loadingUnits,
    loadingReaders,
    showHelpModal,
    isSubmitting
  }

  return {
    states,
    handlers
  }
}
